// deps
import React, { ReactElement, memo, useCallback, useMemo } from "react";

// internal-deps
import { VALID_WALLET_TYPES } from "@swiggy-private/payment-core";
import { format } from "@swiggy-private/core-ui";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// interfaces
import { WalletPaymentMethodData } from "@payment-ui/interfaces/Wallet";

// components
import AddMoney from "./AddMoney";

// helpers
import { isAddMoneyEligible, isAmazonWallet } from "@payment-ui/helpers/Wallet";

// constants
import { TEXTS } from "@payment-ui/constants/index";

// test-ids
import { WalletTestIds } from "@swiggy-private/payment-test-ids";

import styles from "../Wallet.v4.scss";

interface PayButtonProps {
    walletBalanceLoading: boolean;
    methodData: WalletPaymentMethodData;
    hasSufficientBalance: boolean;
    additionalBalanceNeeded: number;
    onPay: () => void;
    onAddMoney: (value?: number) => void;
    addMoneyImpressionEvent: VoidFunction;
    walletTestIds: WalletTestIds;
    paymentAmount: string;
}

function getButtonSubText(walletName: string): string {
    if (VALID_WALLET_TYPES.PHONEPE === walletName.toUpperCase()) {
        return format(TEXTS.WALLET_REDIRECT_NOTE, walletName);
    }

    return "";
}

function getButtonText({
    walletName,
    additionalBalanceNeeded,
    paymentAmount,
}: {
    walletName: string;
    additionalBalanceNeeded: number;
    paymentAmount: string;
}) {
    const amountToShow = additionalBalanceNeeded > 0 ? additionalBalanceNeeded : paymentAmount;
    const title = format(TEXTS.PAY_X_WITH_Y, String(amountToShow), walletName);
    const subText = getButtonSubText(walletName);

    return {
        title,
        subText,
    };
}

function PayButton(props: PayButtonProps): ReactElement | null {
    const { onPay, onAddMoney, addMoneyImpressionEvent, walletTestIds } = props;
    const canShowAddMoney = useMemo((): boolean => {
        return !props.hasSufficientBalance && !isAmazonWallet(props.methodData) && isAddMoneyEligible(props.methodData);
    }, [props.hasSufficientBalance, props.methodData]);

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            e.stopPropagation();
            onPay();
        },
        [onPay],
    );

    const onAddMoneyClick = useCallback(
        (value: number): void => {
            value > 0 && onAddMoney(value);
        },
        [onAddMoney],
    );

    if (props.walletBalanceLoading) {
        return null;
    }

    if (canShowAddMoney) {
        return (
            <AddMoney
                additionalBalanceNeeded={props.additionalBalanceNeeded}
                onClick={onAddMoneyClick}
                addMoneyImpressionEvent={addMoneyImpressionEvent}
                walletTestIds={walletTestIds}
            />
        );
    }

    const { title, subText } = getButtonText({
        walletName: props.methodData.name,
        additionalBalanceNeeded: props.additionalBalanceNeeded,
        paymentAmount: props.paymentAmount,
    });

    return (
        <div className={styles.paymentMethodPayWrapper}>
            <SuperButton
                variant="fullwidth"
                buttonType="specialPositive"
                buttonSize="medium"
                className={styles.paymentMethodPayButton}
                onClick={onClick}
                data-testid={walletTestIds.PAY_BUTTON}
                subLabel={subText}
            >
                {title}
            </SuperButton>
        </div>
    );
}

export default memo(PayButton);
